import axios from 'axios'

// Get API URL from environment variables
const getApiUrl = () => {
  // Remove any trailing slashes to prevent CORS issues
  const baseUrl = process.env.REACT_APP_API_URL || 'https://leafwel-backend-2vet.vercel.app'
  return baseUrl.replace(/\/$/, '')
}

const API_BASE_URL = getApiUrl()

console.log('🔗 Using API URL:', API_BASE_URL)

// Create axios instance with proper configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
  },
  // Don't use credentials for CORS simplicity
  withCredentials: false,
})

// Request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    console.log('🌐 API Request:', config.method?.toUpperCase(), config.url)
    console.log('🔑 Headers:', config.headers)
    if (config.data) {
      console.log('📤 Request Data:', config.data)
    }
    return config
  },
  (error) => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for debugging and error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.url)
    console.log('📥 Response Data:', response.data)
    return response
  },
  (error) => {
    console.error('❌ API Response Error:', error)
    
    if (error.response) {
      // Server responded with error status
      console.error('❌ Response Status:', error.response.status)
      console.error('❌ Response Data:', error.response.data)
      console.error('❌ Response Headers:', error.response.headers)
    } else if (error.request) {
      // Request was made but no response received
      console.error('❌ No response received:', error.request)
    } else {
      // Something else happened
      console.error('❌ Request setup error:', error.message)
    }
    
    return Promise.reject(error)
  }
)

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  try {
    const config = {
      url: endpoint,
      method: options.method || 'GET',
      ...options,
    }

    if (options.data) {
      config.data = options.data
    }

    const response = await apiClient(config)
    return response.data
  } catch (error) {
    // Enhanced error handling
    if (error.response) {
      // Server error response
      const errorMessage = error.response.data?.message || error.response.data?.error || `HTTP ${error.response.status} Error`
      throw new Error(errorMessage)
    } else if (error.request) {
      // Network error
      throw new Error('Network Error: Unable to connect to server. Please check your internet connection.')
    } else {
      // Other error
      throw new Error(error.message || 'An unexpected error occurred')
    }
  }
}

// API endpoints
export const API_ENDPOINTS = {
  health: '/api/health',
  testStripe: '/api/test-stripe',
  stripeCreatePaymentIntent: '/api/stripe/create-payment-intent',
  stripeConfirmPayment: '/api/stripe/confirm-payment',
  stripePayments: '/api/stripe/payments',
  bookings: '/api/bookings',
  contact: '/api/contact',
}

// Health API
export const healthAPI = {
  check: () => apiCall(API_ENDPOINTS.health),
}

// Stripe API
export const stripeAPI = {
  testStripe: () => apiCall(API_ENDPOINTS.testStripe),
  createPaymentIntent: (data) => apiCall(API_ENDPOINTS.stripeCreatePaymentIntent, {
    method: 'POST',
    data,
  }),
  confirmPayment: (data) => apiCall(API_ENDPOINTS.stripeConfirmPayment, {
    method: 'POST',
    data,
  }),
  getPayments: () => apiCall(API_ENDPOINTS.stripePayments),
}

// Booking API
export const bookingAPI = {
  create: (data) => apiCall(API_ENDPOINTS.bookings, {
    method: 'POST',
    data,
  }),
  getAll: () => apiCall(API_ENDPOINTS.bookings),
  getById: (id) => apiCall(`${API_ENDPOINTS.bookings}/${id}`),
}

// Contact API
export const contactAPI = {
  submit: (data) => apiCall(API_ENDPOINTS.contact, {
    method: 'POST',
    data,
  }),
}

// Configuration info for debugging
export const getApiConfig = () => ({
  environment: process.env.NODE_ENV || 'development',
  apiUrl: API_BASE_URL,
  hasStripeKey: !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  usingCustomApiUrl: !!process.env.REACT_APP_API_URL,
})

console.log('🔗 API Configuration:', getApiConfig())

// Export the axios instance for direct use if needed
export { apiClient }
export default apiCall
